import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdAdd, MdArrowBack, MdAutoAwesome, MdNotificationsNone, MdPerson, MdSummarize, MdSwapHoriz, MdVerifiedUser } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  FileViewerModal,
  InlineWarning,
  OutlineButton,
  PageLoader,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
} from '../../../components/ui';
import {
  getAppUser,
  revokeAppUserBu,
  syncAppUserBu,
} from '../../../repositories/appUser.repository';
import { swapAadhaarDocuments } from '../../../repositories/document.repository';
import {
  lookupBusinessUnits,
  lookupSites,
} from '../../../repositories/lookup.repository';
import { ageYearsMonthsFromDob, formatDateDisplay, formatDateTimeDisplay } from '../../../utils/personName';
import { profilePhotoOnly } from '../../../utils/profilePhoto';
import { AiKycVerifyModal } from './AiKycVerifyModal';
import { AppUserVerificationModal } from './AppUserVerificationModal';
import { GstLookupModal } from './GstLookupModal';
import { MaterialSupplyDetailModal } from './MaterialSupplyDetailModal';
import { MemberEquipmentDetailModal } from './MemberEquipmentDetailModal';
import { MemberActivitySummaryModal } from './MemberActivitySummaryModal';
import { SendNotificationModal } from './SendNotificationModal';

function FieldCard({ label, value, hideIfEmpty = false, action = null, children = null }) {
  if (
    !children &&
    hideIfEmpty &&
    (value == null || String(value).trim() === '' || value === '—')
  ) {
    return null;
  }
  return (
    <Box
      px={3}
      py={2}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      w="100%"
    >
      <Flex align="center" justify="space-between" gap={2} mb={0.5}>
        <Text fontSize="11px" color="text.secondary" fontWeight="500" lineHeight="1.2">
          {label}
        </Text>
        {action}
      </Flex>
      {children || (
        <Text fontSize="14px" fontWeight="600" whiteSpace="pre-wrap" lineHeight="1.35">
          {value || '—'}
        </Text>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Heading size="sm" mb={3}>
      {children}
    </Heading>
  );
}

function EmptyNote({ children }) {
  return (
    <Text fontSize="14px" color="text.secondary">
      {children}
    </Text>
  );
}

function DocumentsGrid({ docs, emptyLabel, onOpen }) {
  if (!docs?.length) {
    return <EmptyNote>{emptyLabel}</EmptyNote>;
  }
  return (
    <SimpleGrid columns={2} spacing={2}>
      {docs.map((doc) => {
        const file = toViewerFile(doc);
        return (
          <Flex
            key={doc.id || doc.docType}
            p={2}
            minH="52px"
            bg="bg.input"
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            align="center"
            gap={1.5}
            justify="space-between"
            cursor={file ? 'pointer' : 'default'}
            onClick={() => file && onOpen(file)}
            _hover={file ? { borderColor: 'utkarsh.amber' } : undefined}
          >
            <Box minW={0} flex="1">
              <Text fontWeight="600" fontSize="12px" noOfLines={1}>
                {docLabel(doc.docType)}
              </Text>
              <Text
                fontSize="11px"
                color="text.secondary"
                fontFamily={doc.documentNumber ? 'mono' : 'inherit'}
                mt={0.5}
                noOfLines={1}
              >
                {doc.documentNumber || 'Uploaded'}
              </Text>
            </Box>
            {file ? (
              <Text
                fontSize="12px"
                fontWeight="700"
                color="accent.amber"
                flexShrink={0}
              >
                Open
              </Text>
            ) : (
              <Text fontSize="11px" color="text.secondary" flexShrink={0}>
                No file
              </Text>
            )}
          </Flex>
        );
      })}
    </SimpleGrid>
  );
}

/** Clickable summary tile for seller catalogue categories. */
function CatalogueSummaryTile({
  title,
  countLabel,
  hint,
  enabled,
  onClick,
  previewItems,
}) {
  const labels = (Array.isArray(previewItems) ? previewItems : [])
    .map((item) => (typeof item === 'string' ? item : item?.name || item?.label || ''))
    .map((s) => String(s).trim())
    .filter(Boolean);
  const preview = labels.slice(0, 3);
  const extra = labels.length > 3 ? labels.length - 3 : 0;

  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="100%"
      p={3}
      minH="88px"
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      cursor={enabled ? 'pointer' : 'default'}
      opacity={enabled ? 1 : 0.72}
      transition="border-color 0.15s, background 0.15s"
      _hover={
        enabled ? { borderColor: 'brand.500', bg: 'bg.amberTint' } : undefined
      }
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
    >
      <Text fontSize="11px" color="text.secondary" fontWeight="500" mb={1}>
        {title}
      </Text>
      <Flex align="center" gap={1.5} mb={1} flexWrap="wrap">
        <Text fontSize="22px" fontWeight="700" lineHeight="1.2" mr={0.5}>
          {countLabel}
        </Text>
        {preview.map((label) => (
          <Tag
            key={label}
            size="sm"
            borderRadius="full"
            variant="subtle"
            colorScheme="orange"
            maxW="120px"
          >
            <TagLabel noOfLines={1}>{label}</TagLabel>
          </Tag>
        ))}
        {extra > 0 ? (
          <Tag size="sm" borderRadius="full" variant="outline" colorScheme="gray">
            <TagLabel>+{extra} more</TagLabel>
          </Tag>
        ) : null}
      </Flex>
      <Text fontSize="12px" color={enabled ? 'brand.600' : 'text.secondary'} fontWeight="500">
        {hint}
      </Text>
    </Box>
  );
}

function statusBadgeProps(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected') return { status: 'error' };
  return { status: 'pending' };
}

function materialStatusLabel(status) {
  if (status === 'verified') return 'Verified';
  if (status === 'rejected') return 'Rejected';
  return 'Under review';
}

function docLabel(docType) {
  const map = {
    aadhaar_front: 'Aadhaar front',
    aadhaar_back: 'Aadhaar back',
    pan: 'PAN',
    election_card: 'Election card',
    driving_license: 'Driving license',
    profile_photo: 'Profile photo',
    material_image: 'Material image',
    gst_certificate: 'GST certificate',
    business_photo: 'Business photo',
    agency_logo: 'Logo',
  };
  return map[docType] || String(docType || 'Document').replace(/_/g, ' ');
}

function formatDateTime(value) {
  return formatDateTimeDisplay(value);
}

function formatDate(value) {
  return formatDateDisplay(value);
}

/** Format Aadhaar as groups of 4 digits: 5969 7493 3691 */
function formatAadhaarNumber(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function hasOffering(offerings, id) {
  return (offerings || []).includes(id);
}

const BUYER_DOC_TYPES = new Set(['gst_certificate', 'business_photo', 'agency_logo']);

/** Keep newest doc per type (avoid duplicate Aadhaar rows). */
function uniqueDocsByType(docs) {
  const map = new Map();
  for (const doc of docs || []) {
    const key = doc.docType || `id-${doc.id}`;
    const prev = map.get(key);
    if (!prev || Number(doc.id || 0) >= Number(prev.id || 0)) {
      map.set(key, doc);
    }
  }
  return [...map.values()];
}

function docUrl(doc) {
  return doc?.url || doc?.publicUrl || '';
}

function resolveDocKind(doc) {
  const mime = String(doc?.mimeType || '').toLowerCase();
  if (mime.includes('pdf')) return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  const name = String(doc?.fileName || doc?.url || doc?.publicUrl || '').toLowerCase();
  if (name.includes('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(name)) return 'image';
  return 'image';
}

function toViewerFile(doc, labelOverride) {
  const url = docUrl(doc);
  if (!url) return null;
  const kind = resolveDocKind(doc);
  return {
    id: doc.id,
    url,
    label: labelOverride || docLabel(doc.docType),
    fileName:
      doc.fileName ||
      `${doc.docType || 'document'}.${kind === 'pdf' ? 'pdf' : 'jpg'}`,
    mimeType:
      doc.mimeType || (kind === 'pdf' ? 'application/pdf' : 'image/jpeg'),
    kind,
  };
}

export default function AppUserDetailPage({
  userId: userIdProp,
  embedded = false,
  onOpenIndent,
  onOpenEquipment,
  onOpenMember,
} = {}) {
  const { userId: userIdParam } = useParams();
  const userId = userIdProp ?? userIdParam;
  const navigate = useNavigate();
  const toast = useToast();
  const statusModal = useDisclosure();
  const aiVerifyModal = useDisclosure();
  const gstLookupModal = useDisclosure();
  const notifyModal = useDisclosure();
  const summaryModal = useDisclosure();
  const assignModal = useDisclosure();
  const fileViewer = useDisclosure();
  const materialDetailModal = useDisclosure();
  const equipmentDetailModal = useDisclosure();
  const equipmentListModal = useDisclosure();
  const materialListModal = useDisclosure();
  const specialServicesModal = useDisclosure();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewerFile, setViewerFile] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [kycInitialRole, setKycInitialRole] = useState(null);
  const [photoBroken, setPhotoBroken] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedAssignmentKeys, setSelectedAssignmentKeys] = useState(() => new Set());
  const [buBusy, setBuBusy] = useState(false);
  const [aadhaarSwapBusy, setAadhaarSwapBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setPhotoBroken(false);
    setLogoBroken(false);
    try {
      const [data, buRes, siteRes] = await Promise.all([
        getAppUser(userId),
        lookupBusinessUnits().catch(() => []),
        lookupSites().catch(() => []),
      ]);
      setUser(data);
      setBusinessUnits(buRes || []);
      setSites(siteRes || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load user',
        status: 'error',
        position: 'top-right',
      });
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const assignmentKey = (buId, siteId) =>
    `${Number(buId)}:${siteId == null ? 'null' : Number(siteId)}`;

  const buSiteRows = useMemo(() => {
    const buById = new Map((businessUnits || []).map((b) => [Number(b.id), b]));
    return (sites || [])
      .filter((s) => s.active !== false && s.id != null)
      .map((s) => {
        const buDbId = Number(s.buDbId ?? s.buId);
        const bu = buById.get(buDbId);
        return {
          key: assignmentKey(buDbId, s.id),
          buId: buDbId,
          siteId: Number(s.id),
          buName: bu?.shortName || bu?.code || bu?.buId || `BU #${buDbId}`,
          siteName: s.siteName || s.code || s.siteId || `Site #${s.id}`,
        };
      })
      .filter((row) => Number.isFinite(row.buId) && Number.isFinite(row.siteId))
      .sort((a, b) => {
        const byBu = String(a.buName).localeCompare(String(b.buName));
        if (byBu !== 0) return byBu;
        return String(a.siteName).localeCompare(String(b.siteName));
      });
  }, [businessUnits, sites]);

  const buGroups = useMemo(() => {
    const map = new Map();
    (businessUnits || []).forEach((b) => {
      const buId = Number(b.id);
      if (!Number.isFinite(buId)) return;
      map.set(buId, {
        buId,
        buName: b.shortName || b.code || b.buId || `BU #${buId}`,
        buOnlyKey: assignmentKey(buId, null),
        sites: [],
      });
    });
    buSiteRows.forEach((row) => {
      if (!map.has(row.buId)) {
        map.set(row.buId, {
          buId: row.buId,
          buName: row.buName,
          buOnlyKey: assignmentKey(row.buId, null),
          sites: [],
        });
      }
      map.get(row.buId).sites.push(row);
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.buName).localeCompare(String(b.buName)),
    );
  }, [businessUnits, buSiteRows]);

  const openAssignModal = () => {
    const next = new Set(
      (user?.businessUnits || [])
        .filter((a) => a.buId != null)
        .map((a) => assignmentKey(a.buId, a.siteId ?? null)),
    );
    setSelectedAssignmentKeys(next);
    assignModal.onOpen();
  };

  const toggleAssignmentKey = (key) => {
    setSelectedAssignmentKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleBuGroup = (group) => {
    setSelectedAssignmentKeys((prev) => {
      const next = new Set(prev);
      if (!group.sites.length) {
        if (next.has(group.buOnlyKey)) next.delete(group.buOnlyKey);
        else next.add(group.buOnlyKey);
        return next;
      }
      const keys = group.sites.map((s) => s.key);
      const allSelected = keys.every((k) => next.has(k));
      if (allSelected) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
        next.delete(group.buOnlyKey);
      }
      return next;
    });
  };

  const handleSyncAssignments = async () => {
    if (!user?.id) return;
    setBuBusy(true);
    try {
      const assignments = [];
      buGroups.forEach((group) => {
        if (!group.sites.length) {
          if (selectedAssignmentKeys.has(group.buOnlyKey)) {
            assignments.push({ buId: group.buId, siteId: null });
          }
          return;
        }
        group.sites.forEach((row) => {
          if (selectedAssignmentKeys.has(row.key)) {
            assignments.push({ buId: row.buId, siteId: row.siteId });
          }
        });
      });
      const updated = await syncAppUserBu(user.id, assignments);
      setUser(updated);
      assignModal.onClose();
      toast({
        title: 'Assignments saved',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save assignments',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBuBusy(false);
    }
  };

  const handleRevokeBu = async (buId, siteId) => {
    if (!user?.id || !buId) return;
    setBuBusy(true);
    try {
      const updated = await revokeAppUserBu(user.id, buId, siteId);
      setUser(updated);
      toast({
        title: 'Removed',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to remove',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBuBusy(false);
    }
  };

  const assignedBuGroups = useMemo(() => {
    const map = new Map();
    (user?.businessUnits || []).forEach((a) => {
      const buId = Number(a.buId);
      if (!map.has(buId)) {
        map.set(buId, {
          buId,
          buName: a.shortName || `BU #${buId}`,
          isPrimary: Boolean(a.isPrimary),
          sites: [],
        });
      }
      const group = map.get(buId);
      if (a.isPrimary) group.isPrimary = true;
      group.sites.push({
        id: a.id,
        siteId: a.siteId,
        siteName: a.siteName || '—',
        buId,
      });
    });
    return Array.from(map.values());
  }, [user]);

  const docsByType = useMemo(() => uniqueDocsByType(user?.documents), [user]);

  const profileDoc = useMemo(
    () => docsByType.find((d) => d.docType === 'profile_photo') || null,
    [docsByType],
  );

  const logoDoc = useMemo(
    () => docsByType.find((d) => d.docType === 'agency_logo') || null,
    [docsByType],
  );

  /** Prefer signed document URL — raw profilePhotoUrl is often private on S3. */
  const profilePhotoSrc = useMemo(() => {
    const signed = profilePhotoOnly(docUrl(profileDoc));
    if (signed) return signed;
    return profilePhotoOnly(user?.profilePhotoUrl);
  }, [profileDoc, user]);

  const logoSrc = useMemo(() => {
    const signed = docUrl(logoDoc);
    if (signed) return signed;
    return user?.agencyLogoUrl || '';
  }, [logoDoc, user]);

  /** Shared identity docs (Aadhaar, PAN, etc.) — not role-specific. */
  const identityDocs = useMemo(
    () =>
      docsByType.filter(
        (d) =>
          d.docType !== 'profile_photo' &&
          d.docType !== 'agency_logo' &&
          d.docType !== 'material_image' &&
          !BUYER_DOC_TYPES.has(d.docType),
      ),
    [docsByType],
  );

  const aadhaarDisplay = useMemo(() => {
    const front = docsByType.find((d) => d.docType === 'aadhaar_front');
    const back = docsByType.find((d) => d.docType === 'aadhaar_back');
    const raw = front?.documentNumber || back?.documentNumber || '';
    return formatAadhaarNumber(raw);
  }, [docsByType]);

  /** Buyer / agency documents (GST certificate, business photo). Logo shown in header. */
  const buyerDocs = useMemo(
    () =>
      docsByType.filter(
        (d) => BUYER_DOC_TYPES.has(d.docType) && d.docType !== 'agency_logo',
      ),
    [docsByType],
  );

  const openFile = (file) => {
    if (!file?.url) return;
    setViewerFile(file);
    fileViewer.onOpen();
  };

  const openKycStatus = (role = null) => {
    setKycInitialRole(role);
    statusModal.onOpen();
  };

  const handleSwapAadhaar = async () => {
    const front = docsByType.find((d) => d.docType === 'aadhaar_front');
    const back = docsByType.find((d) => d.docType === 'aadhaar_back');
    if (!front || !back || !user?.id) {
      toast({
        title: 'Upload both Aadhaar sides before swapping',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    setAadhaarSwapBusy(true);
    try {
      await swapAadhaarDocuments({ ownerType: 'user', ownerId: user.id });
      toast({
        title: 'Aadhaar front and back swapped',
        status: 'success',
        position: 'top-right',
      });
      await load();
    } catch (err) {
      toast({
        title: err?.message || 'Swap failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setAadhaarSwapBusy(false);
    }
  };

  const openMaterialDetail = (item) => {
    setSelectedMaterial(item);
    materialListModal.onClose();
    materialDetailModal.onOpen();
  };

  const openEquipmentDetail = (item) => {
    setSelectedEquipment(item);
    equipmentListModal.onClose();
    equipmentDetailModal.onOpen();
  };

  const openMaterialImage = (item) => {
    if (!item?.imageUrl) return;
    openFile({
      id: `material-${item.id}`,
      url: item.imageUrl,
      label: item.name || 'Material image',
      fileName: `${String(item.name || 'material').replace(/\s+/g, '_').toLowerCase()}.jpg`,
      mimeType: 'image/jpeg',
      kind: 'image',
    });
  };

  const openEquipmentImage = (url, label) => {
    if (!url) return;
    openFile({
      id: `eq-img-${Date.now()}`,
      url,
      label: label || 'Equipment image',
      fileName: 'equipment.jpg',
      mimeType: 'image/jpeg',
      kind: 'image',
    });
  };

  if (loading) return <PageLoader label="Loading user…" />;
  if (!user) {
    return (
      <Box>
        {!embedded ? (
          <OutlineButton leftIcon={<MdArrowBack />} onClick={() => navigate('/admin/users')}>
            Back to users
          </OutlineButton>
        ) : null}
        <Text mt={embedded ? 0 : 6} color="text.secondary">
          User not found.
        </Text>
      </Box>
    );
  }

  const address = [
    user.address1,
    user.address2,
    user.village,
    user.taluka,
    user.district,
    user.state,
    user.pin,
  ]
    .filter(Boolean)
    .join(', ');

  const offersEquipment = hasOffering(user.offerings, 'equipment');
  const offersMaterial = hasOffering(user.offerings, 'material_supply');
  const offersSpecial = hasOffering(user.offerings, 'special_services');
  const materials = user.materials || [];
  const equipments = user.equipments || [];
  const specialServices =
    (user.specialServices?.length
      ? user.specialServices
      : user.roles?.service_provider?.specialServices) || [];
  const canOpenPhoto = Boolean(profilePhotoSrc) && !photoBroken;
  const canOpenLogo = Boolean(logoSrc) && !logoBroken;
  const ageLabel = ageYearsMonthsFromDob(user.dob);
  const dobDisplay = user.dob
    ? `${formatDate(user.dob)}${ageLabel ? ` · ${ageLabel}` : ''}`
    : '';
  const isAgency = user.userType === 'agency';
  const buyerRole = user.roles?.agency || null;
  const sellerRole = user.roles?.service_provider || null;
  const buyerStatus = buyerRole?.verificationStatus
    ? buyerRole.verificationStatus.charAt(0).toUpperCase() +
      buyerRole.verificationStatus.slice(1)
    : null;
  const sellerStatus = sellerRole?.verificationStatus
    ? sellerRole.verificationStatus.charAt(0).toUpperCase() +
      sellerRole.verificationStatus.slice(1)
    : null;

  const buyerTradeName = String(buyerRole?.tradeName || user.tradeName || '').trim();
  const buyerGstNumber = String(buyerRole?.gstNumber || user.gstNumber || '').trim();
  const buyerConstitution = String(
    buyerRole?.constitution || user.constitution || '',
  ).trim();
  /** Hide empty buyer UI until the member has started buyer setup. */
  const showBuyerSection =
    isAgency ||
    Boolean(buyerTradeName) ||
    Boolean(buyerGstNumber) ||
    Boolean(buyerConstitution) ||
    buyerDocs.length > 0 ||
    Boolean(user.gstDetails) ||
    Boolean(buyerRole?.isComplete) ||
    Boolean(buyerRole?.rejectionReason) ||
    (Array.isArray(user.businessUnits) && user.businessUnits.length > 0);

  /** BU assign only for verified buyers (keep visible if already assigned). */
  const showBuSection =
    (showBuyerSection && buyerStatus === 'Verified') ||
    assignedBuGroups.length > 0;

  const ownerLabel =
    user.ownerName ||
    [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ') ||
    user.fullName ||
    `User #${user.id}`;
  const displayTitle = ownerLabel;
  const companyEmail = String(buyerRole?.companyEmail || '').trim();
  const personalEmail = String(user.email || '').trim();
  /** Real seller = chose offerings / completed seller profile — not an empty pending row. */
  const sellerOfferings = Array.isArray(sellerRole?.offerings)
    ? sellerRole.offerings
    : Array.isArray(user.offerings)
      ? user.offerings
      : [];
  const sellerSpecialServices = Array.isArray(sellerRole?.specialServices)
    ? sellerRole.specialServices
    : Array.isArray(user.specialServices)
      ? user.specialServices
      : [];
  const hasSeller =
    Boolean(sellerRole?.isComplete) ||
    sellerOfferings.length > 0 ||
    sellerSpecialServices.length > 0 ||
    offersEquipment ||
    offersMaterial ||
    offersSpecial ||
    (Array.isArray(equipments) && equipments.length > 0) ||
    (Array.isArray(materials) && materials.length > 0);
  const hasBuyer = showBuyerSection;
  const accountTypeLabel =
    hasSeller && hasBuyer
      ? 'Seller + Buyer'
      : hasBuyer
        ? 'Buyer only'
        : hasSeller
          ? 'Seller only'
          : 'User only';
  const accountTypeHint =
    hasSeller && hasBuyer
      ? `Has both role setups. Currently using ${isAgency ? 'Buyer' : 'Seller'}.`
      : hasBuyer
        ? 'Agency / buyer details are set up. Seller catalogue not started yet.'
        : hasSeller
          ? 'Seller offerings are set up. No agency / buyer profile yet.'
          : 'Basic registration only — no role profile completed yet.';
  const activeRoleLabel = isAgency ? 'Buyer' : hasSeller ? 'Seller' : 'User';

  return (
    <Box>
      {user.verificationStatus === 'Rejected' && user.rejectionReason ? (
        <Box mb={4}>
          <StatusAlertCard variant="error" title="Rejected" body={user.rejectionReason} />
        </Box>
      ) : null}

      <Box mb={4}>
        <Flex
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap={2}
          px={0.5}
          mb={2.5}
        >
          <Flex align="center" gap={2} wrap="wrap">
            <Text fontSize="12px" fontWeight="600" color="text.secondary">
              Account type
            </Text>
            <Tooltip label={accountTypeHint} hasArrow placement="top" openDelay={200}>
              <Tag
                size="md"
                bg="bg.input"
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="full"
                cursor="help"
              >
                <TagLabel fontWeight="700">{accountTypeLabel}</TagLabel>
              </Tag>
            </Tooltip>
            <Tag
              size="sm"
              bg="accent.amber"
              color="text.onAmber"
              borderRadius="full"
            >
              <TagLabel fontWeight="700">
                Active: {activeRoleLabel}
              </TagLabel>
            </Tag>
          </Flex>
          <Flex gap={2} wrap="wrap" w={{ base: '100%', md: 'auto' }}>
            <OutlineButton
              size="sm"
              leftIcon={<MdSummarize />}
              onClick={summaryModal.onOpen}
            >
              Summary
            </OutlineButton>
            <OutlineButton
              size="sm"
              leftIcon={<MdNotificationsNone />}
              onClick={notifyModal.onOpen}
            >
              Notify
            </OutlineButton>
            <OutlineButton
              size="sm"
              leftIcon={<MdAutoAwesome />}
              onClick={aiVerifyModal.onOpen}
            >
              {user.verificationStatus === 'Verified' ? 'AI summary' : 'Verify'}
            </OutlineButton>
          </Flex>
        </Flex>

        <Flex
          gap={3}
          direction={{ base: 'column', lg: 'row' }}
          align="flex-start"
        >
          {/* Left — role cards */}
          <VStack spacing={2.5} align="stretch" flex="1" minW={0}>
            <AppCard
              p={3}
              mb={0}
              bg={!isAgency && hasSeller ? 'rgba(249, 177, 1, 0.06)' : undefined}
              borderColor={!isAgency && hasSeller ? 'accent.amber' : undefined}
            >
              <Flex gap={3} align="flex-start">
                <Box
                  as="button"
                  type="button"
                  flexShrink={0}
                  w="56px"
                  h="56px"
                  borderRadius="md"
                  overflow="hidden"
                  bg="bg.input"
                  borderWidth="1px"
                  borderColor="border.subtle"
                  cursor={canOpenPhoto ? 'pointer' : 'default'}
                  onClick={() => {
                    if (!canOpenPhoto) return;
                    openFile(
                      toViewerFile(profileDoc, 'Profile photo') || {
                        url: profilePhotoSrc,
                        label: 'Profile photo',
                        fileName: 'profile-photo.jpg',
                        mimeType: 'image/jpeg',
                        kind: 'image',
                      },
                    );
                  }}
                  aria-label="View profile photo"
                >
                  {canOpenPhoto ? (
                    <Image
                      src={profilePhotoSrc}
                      alt=""
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      onError={() => setPhotoBroken(true)}
                    />
                  ) : (
                    <Flex align="center" justify="center" h="100%" color="text.secondary">
                      <MdPerson size={22} />
                    </Flex>
                  )}
                </Box>

                <Box minW={0} flex="1">
                  <Flex
                    align={{ base: 'stretch', sm: 'flex-start' }}
                    direction={{ base: 'column', sm: 'row' }}
                    gap={2}
                  >
                    <Box minW={0} flex="1" w="100%">
                      <Flex align="center" gap={1.5} wrap="wrap" mb={0.5}>
                        <Text
                          fontSize="11px"
                          fontWeight="700"
                          color="text.secondary"
                          textTransform="uppercase"
                          letterSpacing="0.04em"
                          lineHeight="1.2"
                        >
                          {hasSeller ? 'Seller · Person' : 'Person'}
                        </Text>
                        {!isAgency && hasSeller ? (
                          <Tag size="sm" bg="accent.amber" color="text.onAmber" borderRadius="full">
                            <TagLabel fontWeight="700">Active now</TagLabel>
                          </Tag>
                        ) : null}
                      </Flex>
                      <Heading size="sm" noOfLines={2} lineHeight="1.25" mb={1}>
                        {displayTitle}
                      </Heading>
                      {address ? (
                        <Text
                          fontSize="12px"
                          color="text.secondary"
                          noOfLines={2}
                          mb={1}
                          title={address}
                        >
                          {address}
                        </Text>
                      ) : null}
                      <Flex align="baseline" gap={2} wrap="wrap" rowGap={0.5}>
                        <Text fontSize="13px" fontWeight="600">
                          {user.phone || '—'}
                        </Text>
                        {user.lastOtpCode ? (
                          <Tooltip
                            label={`Sent ${formatDateTime(user.lastOtpSentAt)}`}
                            hasArrow
                            placement="top"
                            openDelay={200}
                          >
                            <Text
                              as="span"
                              fontSize="12px"
                              fontWeight="600"
                              color="accent.amber"
                              cursor="help"
                              borderBottom="1px dashed"
                              borderColor="accent.amber"
                            >
                              OTP: {user.lastOtpCode}
                            </Text>
                          </Tooltip>
                        ) : null}
                        {personalEmail ? (
                          <Text
                            fontSize="13px"
                            color="text.secondary"
                            noOfLines={2}
                            wordBreak="break-word"
                          >
                            {personalEmail}
                          </Text>
                        ) : null}
                      </Flex>
                    </Box>
                    {hasSeller ? (
                      <Flex
                        align="center"
                        gap={2}
                        flexShrink={0}
                        wrap="wrap"
                        justify={{ base: 'flex-start', sm: 'flex-end' }}
                        w={{ base: '100%', sm: 'auto' }}
                      >
                        {sellerStatus ? (
                          <StatusBadge {...statusBadgeProps(sellerStatus)}>
                            {sellerStatus}
                          </StatusBadge>
                        ) : null}
                        <PrimaryButton
                          size="xs"
                          leftIcon={<MdVerifiedUser />}
                          onClick={() => openKycStatus('service_provider')}
                        >
                          Update
                        </PrimaryButton>
                      </Flex>
                    ) : null}
                  </Flex>
                </Box>
              </Flex>
            </AppCard>

            {hasBuyer ? (
              <AppCard
                p={3}
                mb={0}
                bg={isAgency ? 'rgba(249, 177, 1, 0.06)' : undefined}
                borderColor={isAgency ? 'accent.amber' : undefined}
              >
                <Flex gap={3} align="flex-start">
                  <Box
                    as="button"
                    type="button"
                    flexShrink={0}
                    w="56px"
                    h="56px"
                    borderRadius="md"
                    overflow="hidden"
                    bg="white"
                    borderWidth="1px"
                    borderColor="border.subtle"
                    cursor={canOpenLogo ? 'pointer' : 'default'}
                    onClick={() => {
                      if (!canOpenLogo) return;
                      openFile(
                        toViewerFile(logoDoc, 'Logo') || {
                          url: logoSrc,
                          label: 'Logo',
                          fileName: 'agency-logo.jpg',
                          mimeType: 'image/jpeg',
                          kind: 'image',
                        },
                      );
                    }}
                    aria-label="View agency logo"
                  >
                    {canOpenLogo ? (
                      <Image
                        src={logoSrc}
                        alt=""
                        w="100%"
                        h="100%"
                        objectFit="contain"
                        onError={() => setLogoBroken(true)}
                      />
                    ) : (
                      <Flex align="center" justify="center" h="100%" color="text.secondary">
                        <Text fontSize="xs">Logo</Text>
                      </Flex>
                    )}
                  </Box>

                  <Box minW={0} flex="1">
                    <Flex
                      align={{ base: 'stretch', sm: 'flex-start' }}
                      direction={{ base: 'column', sm: 'row' }}
                      gap={2}
                    >
                      <Box minW={0} flex="1" w="100%">
                        <Flex align="center" gap={1.5} wrap="wrap" mb={0.5}>
                          <Text
                            fontSize="11px"
                            fontWeight="700"
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.04em"
                            lineHeight="1.2"
                          >
                            Buyer · Agency
                          </Text>
                          {isAgency ? (
                            <Tag size="sm" bg="accent.amber" color="text.onAmber" borderRadius="full">
                              <TagLabel fontWeight="700">Active now</TagLabel>
                            </Tag>
                          ) : null}
                        </Flex>
                        <Heading size="sm" noOfLines={2} lineHeight="1.25" mb={1}>
                          {buyerTradeName || 'Agency setup started'}
                        </Heading>
                        <Flex align="center" gap={2} wrap="wrap" mb={1}>
                          {buyerGstNumber ? (
                            <Text fontSize="12px" fontWeight="600" fontFamily="mono">
                              GST {buyerGstNumber}
                            </Text>
                          ) : null}
                          {buyerConstitution ? (
                            <Text fontSize="12px" color="text.secondary">
                              {buyerConstitution}
                            </Text>
                          ) : null}
                        </Flex>
                        {companyEmail ? (
                          <Text
                            fontSize="13px"
                            color="text.secondary"
                            noOfLines={2}
                            wordBreak="break-word"
                          >
                            {companyEmail}
                          </Text>
                        ) : (
                          <Text fontSize="12px" color="text.secondary">
                            No company email yet
                          </Text>
                        )}
                      </Box>
                      <Flex
                        align="center"
                        gap={2}
                        flexShrink={0}
                        wrap="wrap"
                        justify={{ base: 'flex-start', sm: 'flex-end' }}
                        w={{ base: '100%', sm: 'auto' }}
                      >
                        {buyerStatus ? (
                          <StatusBadge {...statusBadgeProps(buyerStatus)}>
                            {buyerStatus}
                          </StatusBadge>
                        ) : null}
                        <PrimaryButton
                          size="xs"
                          leftIcon={<MdVerifiedUser />}
                          onClick={() => openKycStatus('agency')}
                        >
                          Update
                        </PrimaryButton>
                      </Flex>
                    </Flex>
                  </Box>
                </Flex>
              </AppCard>
            ) : null}
          </VStack>

          {/* Right — Activity (content height only — no stretch) */}
          <Box
            flexShrink={0}
            w={{ base: '100%', lg: '240px' }}
            alignSelf="flex-start"
            h="fit-content"
            borderWidth="1px"
            borderColor="border.subtle"
            borderRadius="md"
            overflow="hidden"
            bg="bg.surface"
          >
            <Box px={3} py={1.5} borderBottomWidth="1px" borderColor="border.subtle" bg="bg.input">
              <Text fontSize="11px" fontWeight="700" color="text.secondary" letterSpacing="0.03em">
                ACTIVITY
              </Text>
            </Box>
            <Box px={3} py={1.5} borderBottomWidth="1px" borderColor="border.subtle">
              <Text fontSize="11px" color="text.secondary" lineHeight="1.2" mb={0.5}>
                Registered ({user.registrationStatusLabel || '—'})
              </Text>
              <Text fontSize="12px" fontWeight="600" lineHeight="1.25">
                {formatDateTime(user.createdAt)}
              </Text>
            </Box>
            <Box px={3} py={1.5} borderBottomWidth="1px" borderColor="border.subtle">
              <Text fontSize="11px" color="text.secondary" lineHeight="1.2" mb={0.5}>
                Last updated
              </Text>
              <Text fontSize="12px" fontWeight="600" lineHeight="1.25">
                {formatDateTime(user.updatedAt)}
              </Text>
            </Box>
            <Box px={3} py={1.5}>
              <Text fontSize="11px" color="text.secondary" lineHeight="1.2" mb={0.5}>
                Last login
              </Text>
              <Text fontSize="12px" fontWeight="600" lineHeight="1.25">
                {formatDateTime(user.lastLoginAt)}
              </Text>
            </Box>
          </Box>
        </Flex>
      </Box>

      {showBuSection ? (
        <AppCard mb={4}>
          <Flex justify="space-between" align="center" gap={3} mb={3} wrap="wrap">
            <Heading size="sm" mb={0}>
              Business unit and sites
            </Heading>
            <PrimaryButton
              size="xs"
              h="28px"
              px={3}
              fontSize="13px"
              leftIcon={<MdAdd size={14} />}
              onClick={openAssignModal}
              isDisabled={buBusy || buGroups.length === 0}
            >
              Assign
            </PrimaryButton>
          </Flex>
          {assignedBuGroups.length > 0 ? (
            <Box
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              overflow="hidden"
            >
              <Flex
                px={4}
                py={2.5}
                bg="bg.input"
                borderBottomWidth="1px"
                borderColor="border.subtle"
              >
                <Text flex="1" fontSize="12px" fontWeight="700" letterSpacing="0.04em">
                  BUSINESS UNIT
                </Text>
                <Text flex="1.6" fontSize="12px" fontWeight="700" letterSpacing="0.04em">
                  SITE
                </Text>
              </Flex>
              {assignedBuGroups.map((group) => (
                <Flex
                  key={group.buId}
                  px={4}
                  py={3}
                  align="flex-start"
                  gap={4}
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  _last={{ borderBottomWidth: 0 }}
                >
                  <Box flex="1" minW={0} pt={1}>
                    <Text fontSize="15px" fontWeight="700" noOfLines={1}>
                      {group.buName}
                      {group.isPrimary ? (
                        <Text as="span" fontWeight="500" color="text.secondary">
                          {' '}
                          · Primary
                        </Text>
                      ) : null}
                    </Text>
                  </Box>
                  <Flex flex="1.6" wrap="wrap" gap={2}>
                    {group.sites.map((site) => (
                      <Flex
                        key={site.id || `${site.buId}-${site.siteId}`}
                        align="center"
                        gap={2}
                        px={3}
                        py={2}
                        minH="40px"
                        bg="bg.input"
                        borderRadius="sm"
                        borderWidth="1px"
                        borderColor="border.subtle"
                      >
                        <Text fontSize="14px" fontWeight="600">
                          {site.siteId == null
                            ? 'No site'
                            : site.siteName || '—'}
                        </Text>
                        <Text
                          as="button"
                          type="button"
                          fontSize="12px"
                          fontWeight="600"
                          color="text.secondary"
                          disabled={buBusy}
                          onClick={() => handleRevokeBu(site.buId, site.siteId)}
                          _hover={{ color: 'red.500' }}
                        >
                          Remove
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              ))}
            </Box>
          ) : buyerStatus === 'Verified' ? (
            <InlineWarning>
              Please assign a business unit or site. The buyer can raise requests
              only after this is assigned.
            </InlineWarning>
          ) : null}
        </AppCard>
      ) : null}

      {/* Basic details + identity documents */}
      <Box
        display="grid"
        gridTemplateColumns={{ base: '1fr', xl: 'minmax(0, 1.7fr) minmax(260px, 0.75fr)' }}
        gap={4}
        mb={4}
        alignItems="start"
      >
        <AppCard>
          <SectionTitle>Basic details</SectionTitle>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={2} w="100%">
            <FieldCard label="Owner / name" value={ownerLabel} />
            <FieldCard label="Phone" value={user.phone} />
            <FieldCard
              label={showBuyerSection ? 'Personal email' : 'Email'}
              value={user.email}
            />
            <FieldCard label="Gender" value={user.gender} />
            <FieldCard label="Date of birth" value={dobDisplay} />
            <FieldCard label="Aadhaar card number" value={aadhaarDisplay} />
            <FieldCard label="Education" value={user.education} />
            <FieldCard label="Education details" value={user.educationDetails} />
            <FieldCard label="Secondary contact" value={user.secondaryContact} />
            <FieldCard label="House" value={user.address1} />
            <FieldCard label="Street" value={user.address2} />
            <FieldCard label="Village" value={user.village} />
            <FieldCard label="Taluka" value={user.taluka} />
            <FieldCard label="Dist" value={user.district} />
            <FieldCard label="State" value={user.state} />
            <FieldCard label="PIN" value={user.pin} />
          </SimpleGrid>
        </AppCard>

        <AppCard>
          <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
            <SectionTitle>Identity documents</SectionTitle>
            <OutlineButton
              size="sm"
              leftIcon={<MdSwapHoriz />}
              onClick={handleSwapAadhaar}
              isLoading={aadhaarSwapBusy}
              isDisabled={
                !docsByType.some((d) => d.docType === 'aadhaar_front') ||
                !docsByType.some((d) => d.docType === 'aadhaar_back')
              }
            >
              Swap Aadhaar front / back
            </OutlineButton>
          </Flex>
          <DocumentsGrid
            docs={identityDocs}
            emptyLabel="No identity documents uploaded yet."
            onOpen={openFile}
          />
        </AppCard>
      </Box>

      {/* Seller / service provider — only treat as seller when catalogue/offerings exist */}
      <AppCard mb={4}>
        <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
          <Heading size="sm" mb={0}>
            Seller / service provider details
          </Heading>
          {hasSeller && sellerStatus ? (
            <StatusBadge {...statusBadgeProps(sellerStatus)}>
              {sellerStatus}
            </StatusBadge>
          ) : (
            <StatusBadge status="neutral">Not a seller yet</StatusBadge>
          )}
        </Flex>
        {hasSeller ? (
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={2} w="100%">
              <FieldCard
                label="Profile complete"
                value={sellerRole?.isComplete ? 'Yes' : 'No'}
              />
              <FieldCard
                label="Verification"
                value={sellerStatus || 'Pending'}
                action={
                  <OutlineButton
                    size="xs"
                    onClick={() => openKycStatus('service_provider')}
                  >
                    Update
                  </OutlineButton>
                }
              />
              <FieldCard
                label="Can add catalogue"
                value={user.roles?.canAddCatalog ? 'Yes' : 'No'}
              />
            </SimpleGrid>

            {sellerRole?.rejectionReason ? (
              <StatusAlertCard
                variant="error"
                title="Seller verification rejected"
                body={sellerRole.rejectionReason}
              />
            ) : null}

            {user.roles?.addCatalogBlockReason && !user.roles?.canAddCatalog ? (
              <InlineWarning>{user.roles.addCatalogBlockReason}</InlineWarning>
            ) : null}

            <Box>
              <Text fontSize="13px" fontWeight="700" mb={2}>
                Catalogue
              </Text>
              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2} w="100%">
                <CatalogueSummaryTile
                  title="Equipment"
                  countLabel={
                    offersEquipment || equipments.length
                      ? String(equipments.length)
                      : '—'
                  }
                  hint={
                    !offersEquipment && !equipments.length
                      ? 'Not selected'
                      : equipments.length
                        ? 'Tap to view all equipment'
                        : 'No equipment listed yet'
                  }
                  enabled={offersEquipment || equipments.length > 0}
                  onClick={equipmentListModal.onOpen}
                />
                <CatalogueSummaryTile
                  title="Material supply"
                  countLabel={
                    offersMaterial || materials.length
                      ? String(materials.length)
                      : '—'
                  }
                  hint={
                    !offersMaterial && !materials.length
                      ? 'Not selected'
                      : materials.length
                        ? 'Tap to view materials'
                        : 'No materials listed yet'
                  }
                  enabled={offersMaterial || materials.length > 0}
                  onClick={materialListModal.onOpen}
                />
                <CatalogueSummaryTile
                  title="Special services"
                  countLabel={
                    offersSpecial || specialServices.length
                      ? String(specialServices.length)
                      : '—'
                  }
                  previewItems={specialServices}
                  hint={
                    !offersSpecial && !specialServices.length
                      ? 'Not selected'
                      : specialServices.length > 3
                        ? 'Tap to view all services'
                        : specialServices.length
                          ? 'Tap to view services'
                          : 'No services selected yet'
                  }
                  enabled={offersSpecial || specialServices.length > 0}
                  onClick={specialServicesModal.onOpen}
                />
              </SimpleGrid>
            </Box>
          </VStack>
        ) : (
          <EmptyNote>
            No seller catalogue yet. This member is not treated as a seller until they
            choose offerings (equipment / material / special services).
          </EmptyNote>
        )}
      </AppCard>

      {/* Buyer / agency — only when buyer setup has started */}
      {showBuyerSection ? (
        <>
          <Box
            display="grid"
            gridTemplateColumns={{
              base: '1fr',
              xl: 'minmax(0, 1.7fr) minmax(260px, 0.75fr)',
            }}
            gap={4}
            mb={4}
            alignItems="start"
          >
            <AppCard>
              <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
                <Heading size="sm" mb={0}>
                  Buyer / agency details
                </Heading>
                {buyerStatus ? (
                  <StatusBadge {...statusBadgeProps(buyerStatus)}>
                    {buyerStatus}
                  </StatusBadge>
                ) : (
                  <StatusBadge status="pending">Not started</StatusBadge>
                )}
              </Flex>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={2} w="100%">
                <FieldCard label="Agency / trade name" value={buyerTradeName} />
                <FieldCard
                  label="Company email ID"
                  value={buyerRole?.companyEmail}
                />
                <FieldCard
                  label="GST number"
                  value={buyerGstNumber}
                  action={
                    <OutlineButton size="xs" onClick={gstLookupModal.onOpen}>
                      Fetch
                    </OutlineButton>
                  }
                />
                <FieldCard label="Constitution" value={buyerConstitution} />
                <FieldCard
                  label="Employee code number"
                  value={buyerRole?.employeeCode}
                  action={
                    <OutlineButton size="xs" onClick={() => openKycStatus('agency')}>
                      Update
                    </OutlineButton>
                  }
                />
                <FieldCard
                  label="Profile complete"
                  value={buyerRole?.isComplete ? 'Yes' : 'No'}
                />
                <FieldCard
                  label="Verification"
                  value={buyerStatus || 'Pending'}
                  action={
                    <OutlineButton size="xs" onClick={() => openKycStatus('agency')}>
                      Update
                    </OutlineButton>
                  }
                />
                <FieldCard
                  label="Can raise request"
                  value={user.roles?.canRaiseRequest ? 'Yes' : 'No'}
                />
                <FieldCard
                  label="Primary business unit"
                  value={
                    [user.primaryBuName, user.primarySiteName]
                      .filter(Boolean)
                      .join(' · ') || user.primaryBuName
                  }
                />
              </SimpleGrid>
              {buyerRole?.rejectionReason ? (
                <Box mt={3}>
                  <StatusAlertCard
                    variant="error"
                    title="Buyer verification rejected"
                    body={buyerRole.rejectionReason}
                  />
                </Box>
              ) : null}
              {Array.isArray(buyerRole?.profileUpdateSummary) &&
              buyerRole.profileUpdateSummary.length > 0 ? (
                <Box mt={3}>
                  <FieldCard
                    label="Updated fields"
                    value={buyerRole.profileUpdateSummary.join(', ')}
                  />
                  <Text fontSize="12px" color="text.secondary" mt={1}>
                    Personal profile fields the member changed (buyer KYC returned to pending).
                  </Text>
                </Box>
              ) : null}
              {user.roles?.raiseRequestBlockReason && !user.roles?.canRaiseRequest ? (
                <Box mt={3}>
                  <InlineWarning>{user.roles.raiseRequestBlockReason}</InlineWarning>
                </Box>
              ) : null}
            </AppCard>

            <AppCard>
              <SectionTitle>Buyer documents</SectionTitle>
              <DocumentsGrid
                docs={buyerDocs}
                emptyLabel="No buyer documents (GST certificate / business photo) yet."
                onOpen={openFile}
              />
            </AppCard>
          </Box>

          <AppCard mb={4}>
            <Flex align="center" justify="space-between" gap={3} mb={3} flexWrap="wrap">
              <Heading size="sm" mb={0}>
                Details as per GST
              </Heading>
              <OutlineButton size="sm" onClick={gstLookupModal.onOpen}>
                {user.gstDetails ? 'Refresh' : 'Fetch'}
              </OutlineButton>
            </Flex>
          {user.gstDetails ? (
            <VStack align="stretch" spacing={4}>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={2} w="100%">
                <FieldCard label="GSTIN" value={user.gstDetails.gstin} />
                <FieldCard label="Status" value={user.gstDetails.status} />
                <FieldCard label="Block status" value={user.gstDetails.blockStatus} />
                <FieldCard label="Legal name" value={user.gstDetails.legalName} />
                <FieldCard label="Trade name" value={user.gstDetails.tradeName} />
                <FieldCard label="Taxpayer type" value={user.gstDetails.taxpayerType} />
                <FieldCard
                  label="Constitution"
                  value={user.gstDetails.businessConstitution}
                />
                <FieldCard
                  label="Registration date"
                  value={user.gstDetails.registrationDate}
                />
                <FieldCard
                  label="Cancellation date"
                  value={user.gstDetails.cancellationDate}
                />
                <FieldCard label="State code" value={user.gstDetails.stateCode} />
                <FieldCard
                  label="State jurisdiction"
                  value={user.gstDetails.stateJurisdiction}
                />
                <FieldCard label="City" value={user.gstDetails.city} />
                <FieldCard label="PIN" value={user.gstDetails.pincode} />
                <Box gridColumn={{ base: 'auto', sm: '1 / -1' }}>
                  <FieldCard label="Address" value={user.gstDetails.address} />
                </Box>
                <Box gridColumn={{ base: 'auto', sm: '1 / -1' }}>
                  <FieldCard
                    label="Nature of business"
                    value={
                      Array.isArray(user.gstDetails.natureOfBusiness)
                        ? user.gstDetails.natureOfBusiness.join(', ')
                        : user.gstDetails.natureOfBusiness
                    }
                  />
                </Box>
                <FieldCard
                  label="Last fetched"
                  value={formatDateTime(user.gstDetails.fetchedAt)}
                />
                <FieldCard
                  label="Last updated"
                  value={formatDateTime(user.gstDetails.updatedAt)}
                />
              </SimpleGrid>

              {user.gstDetails.addressDetails &&
              typeof user.gstDetails.addressDetails === 'object' ? (
                <Box>
                  <Text fontSize="13px" fontWeight="700" mb={2}>
                    Address details
                  </Text>
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={2} w="100%">
                    <FieldCard
                      label="Building number"
                      value={user.gstDetails.addressDetails.building_number}
                    />
                    <FieldCard
                      label="Building name"
                      value={user.gstDetails.addressDetails.building_name}
                    />
                    <FieldCard
                      label="Floor"
                      value={user.gstDetails.addressDetails.floor}
                    />
                    <FieldCard
                      label="Street"
                      value={user.gstDetails.addressDetails.street}
                    />
                    <FieldCard
                      label="Locality"
                      value={user.gstDetails.addressDetails.locality}
                    />
                    <FieldCard
                      label="Landmark"
                      value={user.gstDetails.addressDetails.landmark}
                    />
                    <FieldCard
                      label="District"
                      value={user.gstDetails.addressDetails.district}
                    />
                    <FieldCard
                      label="City"
                      value={user.gstDetails.addressDetails.city}
                    />
                    <FieldCard
                      label="State"
                      value={user.gstDetails.addressDetails.state}
                    />
                    <FieldCard
                      label="PIN"
                      value={user.gstDetails.addressDetails.pincode}
                    />
                    {Object.entries(user.gstDetails.addressDetails)
                      .filter(
                        ([key]) =>
                          ![
                            'building_number',
                            'building_name',
                            'floor',
                            'street',
                            'locality',
                            'landmark',
                            'district',
                            'city',
                            'state',
                            'pincode',
                          ].includes(key)
                      )
                      .map(([key, value]) => (
                        <FieldCard
                          key={key}
                          label={key.replace(/_/g, ' ')}
                          value={
                            value == null
                              ? null
                              : typeof value === 'object'
                                ? JSON.stringify(value)
                                : String(value)
                          }
                        />
                      ))}
                  </SimpleGrid>
                </Box>
              ) : null}
            </VStack>
          ) : (
            <EmptyNote>
              No GST details saved yet. Use Fetch next to GST number (or above) to look
              up and save.
            </EmptyNote>
          )}
          </AppCard>
        </>
      ) : null}

      <AppUserVerificationModal
        isOpen={statusModal.isOpen}
        onClose={() => {
          statusModal.onClose();
          setKycInitialRole(null);
        }}
        user={user}
        onSaved={load}
        initialRole={kycInitialRole}
      />

      <AiKycVerifyModal
        isOpen={aiVerifyModal.isOpen}
        onClose={aiVerifyModal.onClose}
        user={user}
      />

      <GstLookupModal
        isOpen={gstLookupModal.isOpen}
        onClose={gstLookupModal.onClose}
        user={user}
        onSaved={load}
      />

      <SendNotificationModal
        isOpen={notifyModal.isOpen}
        onClose={notifyModal.onClose}
        user={user}
      />

      <MemberActivitySummaryModal
        isOpen={summaryModal.isOpen}
        onClose={summaryModal.onClose}
        userId={user?.id}
        userName={user?.fullName || user?.name}
        onNotify={notifyModal.onOpen}
        onOpenIndent={onOpenIndent}
        onOpenEquipment={onOpenEquipment}
        onOpenMember={onOpenMember}
      />

      <Modal
        isOpen={equipmentListModal.isOpen}
        onClose={equipmentListModal.onClose}
        isCentered
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0}>Equipment ({equipments.length})</ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={6}>
            {equipments.length ? (
              <VStack align="stretch" spacing={3}>
                {equipments.map((item) => (
                  <Flex
                    key={item.id}
                    as="button"
                    type="button"
                    textAlign="left"
                    w="100%"
                    p={3}
                    minH="72px"
                    bg="bg.input"
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="border.subtle"
                    align="center"
                    gap={3}
                    justify="space-between"
                    wrap="wrap"
                    cursor="pointer"
                    _hover={{ borderColor: 'brand.500', bg: 'bg.amberTint' }}
                    onClick={() => openEquipmentDetail(item)}
                  >
                    <Flex align="center" gap={3} minW={0} flex="1">
                      <Box
                        w="56px"
                        h="56px"
                        borderRadius="sm"
                        overflow="hidden"
                        bg="white"
                        borderWidth="1px"
                        borderColor="border.subtle"
                        flexShrink={0}
                      >
                        {item.thumbnailUrl ? (
                          <Image
                            src={item.thumbnailUrl}
                            alt=""
                            w="100%"
                            h="100%"
                            objectFit="cover"
                          />
                        ) : (
                          <Flex w="100%" h="100%" align="center" justify="center">
                            <Text fontSize="11px" color="text.secondary">
                              No img
                            </Text>
                          </Flex>
                        )}
                      </Box>
                      <Box minW={0}>
                        <Text fontWeight="600" fontSize="14px" noOfLines={1}>
                          {item.equipmentTypeName || 'Equipment'}
                        </Text>
                        <Text fontSize="13px" color="text.secondary" mt={1} noOfLines={1}>
                          {[item.manufacturer, item.model, item.regNo]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </Text>
                        <Text fontSize="12px" color="brand.600" mt={1} fontWeight="500">
                          View details
                        </Text>
                      </Box>
                    </Flex>
                    <StatusBadge
                      {...statusBadgeProps(materialStatusLabel(item.kycStatus))}
                    >
                      {materialStatusLabel(item.kycStatus)}
                    </StatusBadge>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <EmptyNote>
                No equipment listed yet. Equipment added from the mobile app will appear
                here.
              </EmptyNote>
            )}
          </ModalBody>
          <ModalFooter>
            <OutlineButton onClick={equipmentListModal.onClose}>Close</OutlineButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={materialListModal.isOpen}
        onClose={materialListModal.onClose}
        isCentered
        size="3xl"
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0}>Material supply ({materials.length})</ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={6}>
            {materials.length ? (
              <VStack align="stretch" spacing={3}>
                {materials.map((item) => (
                  <Flex
                    key={item.id}
                    as="button"
                    type="button"
                    textAlign="left"
                    w="100%"
                    p={3}
                    minH="72px"
                    bg="bg.input"
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="border.subtle"
                    align="center"
                    gap={3}
                    justify="space-between"
                    wrap="wrap"
                    cursor="pointer"
                    _hover={{ borderColor: 'brand.500', bg: 'bg.amberTint' }}
                    onClick={() => openMaterialDetail(item)}
                  >
                    <Flex align="center" gap={3} minW={0} flex="1">
                      <Box
                        w="56px"
                        h="56px"
                        borderRadius="sm"
                        overflow="hidden"
                        bg="white"
                        borderWidth="1px"
                        borderColor="border.subtle"
                        flexShrink={0}
                      >
                        {item.imageUrl ? (
                          <Image
                            src={item.imageUrl}
                            alt={item.name || 'Material'}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                          />
                        ) : (
                          <Flex
                            w="100%"
                            h="100%"
                            align="center"
                            justify="center"
                            color="text.secondary"
                            fontSize="12px"
                          >
                            No img
                          </Flex>
                        )}
                      </Box>
                      <Box minW={0}>
                        <Text fontWeight="600" fontSize="14px" noOfLines={1}>
                          {item.name || '—'}
                        </Text>
                        <Text fontSize="13px" color="text.secondary" mt={1} noOfLines={1}>
                          {[item.category, item.unit].filter(Boolean).join(' · ') || '—'}
                        </Text>
                        <Text fontSize="12px" color="brand.600" mt={1} fontWeight="500">
                          View details
                        </Text>
                      </Box>
                    </Flex>
                    <Flex gap={2} align="center" flexShrink={0}>
                      <StatusBadge
                        {...statusBadgeProps(
                          materialStatusLabel(item.verificationStatus),
                        )}
                      >
                        {materialStatusLabel(item.verificationStatus)}
                      </StatusBadge>
                      <StatusBadge status={item.isActive ? 'verified' : 'error'}>
                        {item.isActive ? 'Available' : 'Not available'}
                      </StatusBadge>
                    </Flex>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <EmptyNote>No materials listed yet.</EmptyNote>
            )}
          </ModalBody>
          <ModalFooter>
            <OutlineButton onClick={materialListModal.onClose}>Close</OutlineButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={specialServicesModal.isOpen}
        onClose={specialServicesModal.onClose}
        isCentered
        size="md"
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0} pb={1}>
            <Text fontSize="18px" fontWeight="700" color="text.primary">
              Special services
            </Text>
            <Text fontSize="13px" fontWeight="500" color="text.secondary" mt={1}>
              {specialServices.length
                ? `${specialServices.length} service${specialServices.length === 1 ? '' : 's'} selected by this seller`
                : 'No services selected yet'}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={2}>
            {specialServices.length ? (
              <VStack align="stretch" spacing={2}>
                {specialServices.map((svc, index) => (
                  <Flex
                    key={svc}
                    align="center"
                    gap={3}
                    px={3}
                    py={3}
                    bg="bg.input"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    <Flex
                      w="28px"
                      h="28px"
                      align="center"
                      justify="center"
                      borderRadius="full"
                      bg="utkarsh.amber"
                      flexShrink={0}
                    >
                      <Text fontSize="13px" fontWeight="700" color="text.onAmber">
                        {index + 1}
                      </Text>
                    </Flex>
                    <Text fontSize="15px" fontWeight="600" color="text.primary" lineHeight="1.35">
                      {svc}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <EmptyNote>No special services selected yet.</EmptyNote>
            )}
          </ModalBody>
          <ModalFooter>
            <PrimaryButton onClick={specialServicesModal.onClose}>Close</PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <MaterialSupplyDetailModal
        isOpen={materialDetailModal.isOpen}
        onClose={() => {
          materialDetailModal.onClose();
          setSelectedMaterial(null);
          materialListModal.onOpen();
        }}
        material={selectedMaterial}
        onViewImage={openMaterialImage}
      />

      <MemberEquipmentDetailModal
        isOpen={equipmentDetailModal.isOpen}
        onClose={() => {
          equipmentDetailModal.onClose();
          setSelectedEquipment(null);
          equipmentListModal.onOpen();
        }}
        equipment={selectedEquipment}
        onUpdated={load}
        onViewImage={openEquipmentImage}
      />

      <Modal
        isOpen={assignModal.isOpen}
        onClose={assignModal.onClose}
        isCentered
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          mx={4}
          my={4}
          maxW={{ base: '96vw', md: '1100px' }}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0}>Assign business unit and site</ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" overflowY="auto" minH={0}>
            {buGroups.length === 0 ? (
              <Text fontSize="14px" color="text.secondary">
                No business units available.
              </Text>
            ) : (
              <Box
                maxH={{ base: '420px', md: '620px' }}
                overflowY="auto"
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
              >
                <Flex
                  px={4}
                  py={3}
                  bg="bg.input"
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  position="sticky"
                  top={0}
                  zIndex={1}
                >
                  <Text flex="1" fontSize="12px" fontWeight="700" letterSpacing="0.04em">
                    BUSINESS UNIT
                  </Text>
                  <Text flex="1.4" fontSize="12px" fontWeight="700" letterSpacing="0.04em">
                    SITE
                  </Text>
                </Flex>
                {buGroups.map((group) => {
                  const hasSites = group.sites.length > 0;
                  const selectedCount = hasSites
                    ? group.sites.filter((s) => selectedAssignmentKeys.has(s.key)).length
                    : selectedAssignmentKeys.has(group.buOnlyKey)
                      ? 1
                      : 0;
                  const allSelected = hasSites
                    ? group.sites.length > 0 && selectedCount === group.sites.length
                    : selectedAssignmentKeys.has(group.buOnlyKey);
                  const someSelected = hasSites && selectedCount > 0 && !allSelected;
                  return (
                    <Flex
                      key={group.buId}
                      px={4}
                      py={3}
                      align="flex-start"
                      gap={4}
                      borderBottomWidth="1px"
                      borderColor="border.subtle"
                      _last={{ borderBottomWidth: 0 }}
                    >
                      <Flex flex="1" align="center" gap={3} minW={0} pt={1}>
                        <Checkbox
                          isChecked={allSelected}
                          isIndeterminate={someSelected}
                          onChange={() => toggleBuGroup(group)}
                          colorScheme="yellow"
                        />
                        <Text
                          as="button"
                          type="button"
                          fontSize="15px"
                          fontWeight="700"
                          textAlign="left"
                          onClick={() => toggleBuGroup(group)}
                          _hover={{ opacity: 0.8 }}
                        >
                          {group.buName}
                        </Text>
                      </Flex>
                      <Flex flex="1.4" wrap="wrap" gap={2}>
                        {!hasSites ? (
                          <Text fontSize="13px" color="text.secondary" pt={1}>
                            No sites — BU only
                          </Text>
                        ) : (
                          group.sites.map((site) => {
                            const checked = selectedAssignmentKeys.has(site.key);
                            return (
                              <Box
                                key={site.key}
                                as="button"
                                type="button"
                                onClick={() => toggleAssignmentKey(site.key)}
                                px={3}
                                py={2}
                                minH="40px"
                                borderRadius="sm"
                                borderWidth="1px"
                                borderColor={checked ? 'utkarsh.amber' : 'border.subtle'}
                                bg={checked ? 'utkarsh.amber' : 'bg.input'}
                                color={checked ? 'text.onAmber' : 'inherit'}
                                fontSize="14px"
                                fontWeight={checked ? '600' : '500'}
                                textAlign="left"
                              >
                                {site.siteName}
                              </Box>
                            );
                          })
                        )}
                      </Flex>
                    </Flex>
                  );
                })}
              </Box>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <OutlineButton type="button" onClick={assignModal.onClose} isDisabled={buBusy}>
              Cancel
            </OutlineButton>
            <PrimaryButton
              type="button"
              onClick={handleSyncAssignments}
              isLoading={buBusy}
              isDisabled={buGroups.length === 0}
            >
              Assign
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <FileViewerModal
        isOpen={fileViewer.isOpen}
        onClose={() => {
          fileViewer.onClose();
          setViewerFile(null);
        }}
        file={viewerFile}
      />
    </Box>
  );
}
